import React, { useEffect, useState } from "react";

import { Alert, Badge, Button, ButtonLink, Disclosure, Progress, cx, ExternalLink } from "../../Ui/index.jsx";
import { apiGet, apiPatch } from "../../Api/Client.js";
import { errorText } from "../../Shared/uiStyles.js";
import { ConfirmRead, Culture, Welcome, WorkingHoursForm } from "./GettingStartedForms.jsx";
import { groupSteps, headline, progress } from "./gettingStarted.js";
import "../../Styles/GettingStarted.css";

// What a person does first: the same list the daily reminder counts (profile_completion.py on the server), shown on
// Home so it is in front of them, with the required steps marked and first. Reading steps are confirmed here and the
// confirmation is kept, so the company admin can see who has read what.

const OPENING_STEPS = ["welcome", "culture", "working_hours"];
const OPEN_LABEL = { welcome: "Watch", working_hours: "Set hours" };
const ADD_LABEL = { bank_details: "Add details", calendar: "Connect" };

function Mark({ done }) {
  // Every step has its box, ticked when done, so a missing box never reads as "nothing to do here".
  return <span aria-hidden="true" className={cx("Gs-Mark", done && "is-done")}>{done ? "✓" : ""}</span>;
}

export function GettingStarted({ employee, onChanged, refreshKey = "" }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    apiGet("/Users/EmployeeProfiles/setup-checklist/").then((res) => { if (live) setData(res); }).catch(() => {});
    return () => { live = false; };
  }, [employee?.id, refreshKey]);

  const accept = (res) => { setData(res); setOpen(""); setError(""); if (onChanged) onChanged(); };
  const confirm = async (step) => {
    setBusy(true);
    setError("");
    try {
      accept(await apiPatch("/Users/EmployeeProfiles/setup-checklist/acknowledge/", { step }));
    } catch (err) {
      setError(errorText(err, "Could not save that. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  if (!data || !Array.isArray(data.items) || data.remaining === 0) return null;
  const { required, optional } = groupSteps(data.items.filter((i) => !i.done));
  const finished = data.items.filter((i) => i.done);
  const { percent } = progress(data.items);

  const renderForm = (item) => {
    if (item.key === "welcome") return <Welcome busy={busy} onConfirm={() => confirm("welcome")} />;
    if (item.key === "culture") return <Culture employee={employee} busy={busy} onConfirm={() => confirm("culture")} />;
    if (item.key === "working_hours") return <WorkingHoursForm saved={data.working_hours} slackAvailable={Boolean(data.slack_available)} onSaved={accept} />;
    return (
      <ConfirmRead label="I have read this and understood it." busy={busy} onConfirm={() => confirm(item.key)}>
        {item.link ? <ExternalLink href={item.link} className="ds-link">Open the document (new tab)</ExternalLink> : null}
      </ConfirmRead>
    );
  };

  const renderStep = (item) => {
    const isOpen = open === item.key;
    const opens = OPENING_STEPS.includes(item.key) || item.kind === "read";
    return (
      <li key={item.key} className="Gs-Step">
        <div className="Gs-Row">
          <Mark done={item.done} />
          <div className="Gs-Text">
            <div className={cx("Gs-Title", item.done && "is-done")}>
              {item.label}
              {!item.done ? <Badge variant={item.required ? "warning" : undefined} className="Gs-Tag">{item.required ? "Required" : "Optional"}</Badge> : null}
            </div>
            {!item.done ? <div className="Gs-Why">{item.why}</div> : null}
            {!item.done && item.how?.length ? (
              <ol className="Gs-How">
                {item.how.map((step, i) => <li key={i}>{step}</li>)}
              </ol>
            ) : null}
          </div>
          {!item.done ? (
            opens ? (
              <Button variant={isOpen ? undefined : "primary"} aria-expanded={isOpen} onClick={() => setOpen(isOpen ? "" : item.key)}>
                {isOpen ? "Close" : OPEN_LABEL[item.key] || "Read"}
              </Button>
            ) : (
              <ButtonLink variant="primary" href={item.link || "/profile/"}>{ADD_LABEL[item.key] || "Add"}</ButtonLink>
            )
          ) : null}
        </div>
        {isOpen && !item.done ? renderForm(item) : null}
      </li>
    );
  };

  return (
    <section aria-label="Getting started" className="ds-card ds-card--flush Gs">
      <header className="Gs-Head">
        <h2>Finish setting up</h2>
        <span className="Gs-Count">{headline(data)}</span>
      </header>
      <Progress value={percent} />
      {error ? <Alert tone="danger" className="Gs-Alert">{error}</Alert> : null}
      <ul className="Gs-List">
        {required.map(renderStep)}
        {optional.length ? <li className="Gs-Divider">Also worth doing</li> : null}
        {optional.map(renderStep)}
      </ul>
      {finished.length ? (
        <Disclosure className="Gs-Done" summary="Done" count={finished.length}>
          <ul className="Gs-List">{finished.map(renderStep)}</ul>
        </Disclosure>
      ) : null}
    </section>
  );
}
